/**
 * Consent for measurement (CONTRACTS §J.2). Sources, in order:
 *  1. `config.consent` passed by the integration (e.g. the Woo plugin reading the WP Consent API server-side);
 *  2. Shopify Customer Privacy API (`Shopify.customerPrivacy.analyticsProcessingAllowed()`);
 *  3. WP Consent API in the page (`wp_has_consent('statistics')`), or its `wp_consent_statistics` cookie when only
 *     `wp_consent_type` is exposed;
 *  4. `window.aica.consent` (plugin config);
 *  otherwise `mode: "unknown"` (no persistent id).
 */
export type ConsentMode = 'granted' | 'denied' | 'unknown' | 'exempt_functional';
export interface Consent {
  analytics: boolean;
  marketing: boolean;
  mode: ConsentMode;
}

export const UNKNOWN_CONSENT: Consent = { analytics: false, marketing: false, mode: 'unknown' };

interface ShopifyPrivacy {
  analyticsProcessingAllowed?: () => boolean;
  marketingAllowed?: () => boolean;
  currentVisitorConsent?: () => { analytics?: string; marketing?: string } | undefined;
}

type ShopifyLoadFeatures = (features: Array<{ name: string; version: string }>, cb: (error?: unknown) => void) => void;

interface ConsentWindow {
  Shopify?: { customerPrivacy?: ShopifyPrivacy; loadFeatures?: ShopifyLoadFeatures };
  wp_has_consent?: (category: string) => boolean;
  wp_consent_type?: string;
  aica?: { consent?: Partial<Consent> };
}

const w = (): ConsentWindow => (typeof window !== 'undefined' ? (window as unknown as ConsentWindow) : {});

function normalize(c: Partial<Consent> | null | undefined): Consent | null {
  if (!c || typeof c !== 'object') return null;
  const analytics = c.analytics === true;
  const marketing = c.marketing === true;
  const mode: ConsentMode = c.mode && ['granted', 'denied', 'unknown', 'exempt_functional'].includes(c.mode) ? c.mode : analytics ? 'granted' : typeof c.analytics === 'boolean' ? 'denied' : 'unknown';
  return { analytics: mode === 'unknown' ? false : analytics, marketing, mode };
}

function cookie(name: string): string | null {
  try {
    const m = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`));
    return m ? decodeURIComponent(m[1]!) : null;
  } catch {
    return null;
  }
}

function fromShopify(cp: ShopifyPrivacy): Consent | null {
  if (typeof cp.analyticsProcessingAllowed !== 'function') return null;
  try {
    const analytics = !!cp.analyticsProcessingAllowed();
    const marketing = typeof cp.marketingAllowed === 'function' ? !!cp.marketingAllowed() : false;
    if (analytics) return { analytics, marketing, mode: 'granted' };
    // not allowed: explicit "no" → denied; banner not answered yet → unknown
    const v = typeof cp.currentVisitorConsent === 'function' ? cp.currentVisitorConsent() : undefined;
    return { analytics: false, marketing, mode: v?.analytics === 'no' ? 'denied' : 'unknown' };
  } catch {
    return null;
  }
}

function fromWordPress(win: ConsentWindow): Consent | null {
  if (typeof win.wp_has_consent === 'function') {
    try {
      const analytics = !!win.wp_has_consent('statistics');
      const marketing = !!win.wp_has_consent('marketing');
      return { analytics, marketing, mode: analytics ? 'granted' : 'denied' };
    } catch {
      return null;
    }
  }
  if (typeof win.wp_consent_type === 'string') {
    // Same storage the WP Consent API script uses: `wp_consent_<category>` = allow | deny.
    const s = cookie('wp_consent_statistics');
    const marketing = cookie('wp_consent_marketing') === 'allow';
    if (s === 'allow') return { analytics: true, marketing, mode: 'granted' };
    if (s === 'deny') return { analytics: false, marketing, mode: 'denied' };
    return { analytics: false, marketing, mode: 'unknown' };
  }
  return null;
}

export function readConsent(explicit?: Partial<Consent> | null): Consent {
  const fromConfig = normalize(explicit);
  if (fromConfig) return fromConfig;
  const win = w();
  const cp = win.Shopify?.customerPrivacy;
  const shopify = cp ? fromShopify(cp) : null;
  if (shopify) return shopify;
  const wp = fromWordPress(win);
  if (wp) return wp;
  return normalize(win.aica?.consent) ?? { ...UNKNOWN_CONSENT };
}

/** Calls `cb` when the CMP reports a consent change (Shopify `visitorConsentCollected`, WP `wp_listen_for_consent_change`). */
export function onConsentChange(cb: () => void): () => void {
  if (typeof document === 'undefined') return () => undefined;
  const handler = () => cb();
  document.addEventListener('visitorConsentCollected', handler);
  document.addEventListener('wp_listen_for_consent_change', handler);
  return () => {
    document.removeEventListener('visitorConsentCollected', handler);
    document.removeEventListener('wp_listen_for_consent_change', handler);
  };
}

/**
 * Shopify exposes `Shopify.customerPrivacy` only after the `consent-tracking-api` feature is loaded (themes without a
 * cookie banner never load it): without it every visitor reads as `unknown` and no `aca_vid` is ever created, even
 * where no banner is required. Loads the feature once and calls `cb` when the API becomes available (no-op elsewhere).
 */
export function ensureShopifyPrivacy(cb: () => void): void {
  const shopify = w().Shopify;
  if (!shopify || shopify.customerPrivacy || typeof shopify.loadFeatures !== 'function') return;
  try {
    shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
      if (!error && w().Shopify?.customerPrivacy) cb();
    });
  } catch {
    /* never break the store */
  }
}
